// src/ui/screens/post/PostScreen.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { db } from '../../../firebase';
import {
  collection,
  query,
  orderBy,
  limit,
  where,
  getDocs,
  startAfter,
  onSnapshot,
} from 'firebase/firestore';
import { propertyCategories } from '../../../utils/propertyCategories';
import { userProfileService } from '../../../services/user/userProfileService';
import statusWantService, { StatusWantType } from '../../../services/statusWantService';
import { useSubscribe } from '../../../utils/subscribeProvider';
import { ListingItem } from './widgets/ListingItem';
import { VideoItem } from '../../../components/PostComponents/VideoItem';
import { HomeAdItem } from './widgets/HomeAdItem';
import { FilterIconButton, FilterState } from './widgets/FilterSheet';
import UserAvatar from '../../../components/UserAvatar';
import StatusWantRing from './widgets/StatusWantItem';
import { DesktopCategorySidebar } from './widgets/DesktopCategorySidebar';
import RecommendedUsersCard from './widgets/RecommendedUsersCard';
import AtomRecommendedUsersCard from './widgets/AtomRecommendedUsersCard';
import PostAddFab, { PostAddOption } from './widgets/PostAddFab';
import WantViewerScreen from './WantScreen';
import StatusViewerScreen from './StatusScreen';

// ── SVG icon imports (src/assets/images/svg) ─────────────────────────────────

import saleIcon from '../../../assets/svg/sale.svg';
import rentIcon from '../../../assets/svg/rent.svg';
import shortStayIcon from '../../../assets/svg/short_stay.svg';
import commercialIcon from '../../../assets/svg/commercial.svg';
import landIcon from '../../../assets/svg/land.svg';
import developmentsIcon from '../../../assets/svg/developments.svg';
import tourismIcon from '../../../assets/svg/tourism.svg';
import servicesIcon from '../../../assets/svg/services.svg';
import manufacturersIcon from '../../../assets/svg/manufacturers.svg';
import suppliersIcon from '../../../assets/svg/suppliers.svg';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
  blue: '#4DA3FF',
  special: '#15B4C4', // Manufacturers / Suppliers accent
};

// index matches Flutter's _kCategorySvgPaths ordering exactly
const CATEGORY_SVG_PATHS = [
  saleIcon,          // 0
  rentIcon,           // 1
  shortStayIcon,      // 2
  commercialIcon,     // 3
  landIcon,           // 4
  developmentsIcon,   // 5
  tourismIcon,        // 6
  servicesIcon,       // 7
  manufacturersIcon,  // 8
  suppliersIcon,      // 9
];

const CARD_HEIGHT = 90; // matches Flutter's _kCardHeight
const GRID_GAP = 'gap-1.5'; // 6px, matches Flutter's _kGridGap

const DESKTOP_RAIL_WIDTH = 76;
const DESKTOP_SIDEBAR_WIDTH = 264;
const DESKTOP_CONTENT_OFFSET = DESKTOP_RAIL_WIDTH + DESKTOP_SIDEBAR_WIDTH; // 340
const DESKTOP_AD_BANNER_HEIGHT = 160;

// Batch size the Atoms feed is paginated in — also the "recommendation
// spacing" unit there (one recommendation slide per batch, at a stable
// seeded offset), matching Flutter's _kFeedPageSize.
const FEED_PAGE_SIZE = 20;

const TAB_PARAM_TO_INDEX = { filatom: 0, posted: 1, atoms: 2 };
const TAB_INDEX_TO_PARAM = ['filatom', 'posted', 'atoms'];

const isSpecialCategory = (title) => title === 'Manufacturers' || title === 'Suppliers';

const buildGreeting = (displayName) => {
  const hour = new Date().getHours();
  let salutation;
  if (hour >= 5 && hour < 12) salutation = 'Good Morning';
  else if (hour >= 12 && hour < 17) salutation = 'Good Afternoon';
  else if (hour >= 17 && hour < 21) salutation = 'Good Evening';
  else salutation = 'Good Night';
  const name = displayName && displayName.length > 0 ? `, ${displayName}!` : '!';
  return `${salutation}${name}`;
};

// ── Helper: deterministic seeded RNG ────────────────────────────────────────
//
// Mirrors Flutter's `math.Random(pageIndex * 104729 + 31)` — the
// recommendation slide's position within a page of Atoms must be stable
// across rebuilds (same doc never moves), so this can't be Math.random().

function seededRandom(seed) {
  let t = seed + 0x6d2b79f5;
  return function () {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

// ── SubCategoryModal ────────────────────────────────────────────────────────

const SubCategoryModal = ({ category, categoryIndex, onSubCategorySelected, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const svgPath = categoryIndex < CATEGORY_SVG_PATHS.length ? CATEGORY_SVG_PATHS[categoryIndex] : null;
  const special = isSpecialCategory(category.title);
  const accentColor = special ? COLORS.special : COLORS.orange;

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleSelect = (sub) => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
      onSubCategorySelected(sub);
    }, 300);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backgroundColor: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(18px)',
      }}
      onClick={handleClose}
    >
      <div
        className={`w-full max-w-sm rounded-2xl border transition-all duration-300 flex flex-col overflow-hidden ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
        style={{
          backgroundColor: '#1A1C21',
          borderColor: `${accentColor}40`,
          maxHeight: '85vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 p-5 pb-0 shrink-0">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${accentColor}1F` }}
          >
            {svgPath ? (
              <img
                src={svgPath}
                alt={category.title}
                style={{
                  width: 22,
                  height: 22,
                  filter: special
                    ? 'invert(58%) sepia(59%) saturate(1074%) hue-rotate(133deg) brightness(94%) contrast(92%)'
                    : 'invert(58%) sepia(82%) saturate(2000%) hue-rotate(0deg) brightness(100%) contrast(101%)',
                }}
              />
            ) : (
              <span style={{ color: accentColor }} className="text-xl">🏠</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-bold text-sm">{category.title}</h3>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
            style={{ backgroundColor: '#2C2F36' }}
          >
            <svg
              style={{ width: 18, height: 18 }}
              className="text-white/60"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="h-px bg-white/5 mx-5 my-4 shrink-0" />

        <div className="overflow-y-auto px-5 pb-5" style={{ maxHeight: '55vh' }}>
          <div className="space-y-2">
            {category.subCategories.map((sub) => (
              <button
                key={sub}
                onClick={() => handleSelect(sub)}
                className="w-full p-3.5 rounded-xl flex items-center gap-3 text-left transition-colors hover:bg-filatom-card-hover"
                style={{ backgroundColor: COLORS.card }}
              >
                <svg className="w-3.5 h-3.5 shrink-0" style={{ color: accentColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-white text-sm font-medium">{sub}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── CategoryCard ────────────────────────────────────────────────────────────

const CategoryCard = ({ category, categoryIndex, onSubCategorySelected }) => {
  const [showModal, setShowModal] = useState(false);
  const svgPath = categoryIndex < CATEGORY_SVG_PATHS.length ? CATEGORY_SVG_PATHS[categoryIndex] : null;
  const special = isSpecialCategory(category.title);
  const accentColor = special ? COLORS.special : COLORS.orange;

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="relative rounded-xl overflow-hidden border w-full"
        style={{
          height: CARD_HEIGHT,
          backgroundColor: COLORS.card,
          borderColor: `${accentColor}1A`,
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at 50% 20%, ${accentColor}21, transparent 70%)`,
          }}
        />
        <div
          className="absolute top-0 left-3 right-3 h-px"
          style={{
            background: `linear-gradient(to right, transparent, ${accentColor}59, transparent)`,
          }}
        />

        <div className="relative w-full h-full flex flex-col items-center justify-center">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center"
            style={{ boxShadow: `0 0 18px ${accentColor}47` }}
          >
            {svgPath ? (
              <img
                src={svgPath}
                alt={category.title}
                style={{
                  width: 30,
                  height: 30,
                  filter: special
                    ? 'invert(58%) sepia(59%) saturate(1074%) hue-rotate(133deg) brightness(94%) contrast(92%)'
                    : 'invert(58%) sepia(82%) saturate(2000%) hue-rotate(0deg) brightness(100%) contrast(101%)',
                }}
              />
            ) : (
              <span style={{ color: accentColor }} className="text-2xl">🏠</span>
            )}
          </div>
          <span className="text-white text-[11px] font-semibold mt-1.5 text-center px-1.5 leading-tight line-clamp-2">
            {category.title}
          </span>
        </div>
      </button>

      {showModal && (
        <SubCategoryModal
          category={category}
          categoryIndex={categoryIndex}
          onSubCategorySelected={onSubCategorySelected}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

// ── TabPill ──────────────────────────────────────────────────────────────────

const TabPill = ({ label, isActive, onTap }) => {
  return (
    <button
      onClick={onTap}
      className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
        isActive
          ? 'bg-filatom-card text-white'
          : 'text-white/45 hover:text-white/70'
      }`}
    >
      {label}
    </button>
  );
};

// ── StatusWantRow ────────────────────────────────────────────────────────────
//
// Current user's own status (if any) first, with a "+" badge that only
// ever opens the add-status flow — then everyone else's active
// status/want rings, unviewed-and-newest first, viewed last (that
// ordering comes from StatusWantService.loadFeed()). Renders nothing
// when there's nothing to show, so the recommended-users card below sits
// flush at the top instead of leaving a gap — mirrors Flutter's
// _buildStatusWantRow exactly.

const StatusWantRow = ({ feed, myUid, locallyViewedDocIds, onOpen, onAddStatus }) => {
  if (!feed || feed.isEmpty) return null;

  // Merges the server-known segments for an item with anything the viewer
  // screens have already told us was viewed this session, so a ring can
  // grey out the instant the person backs out — instead of waiting on (and
  // possibly losing a race with) the next feed reload.
  const effectiveSegments = (item) => {
    if (
      locallyViewedDocIds.size === 0 ||
      !item.docIds ||
      item.docIds.length !== item.segments.length
    ) {
      return item.segments;
    }
    return item.segments.map((seen, i) => seen || locallyViewedDocIds.has(item.docIds[i]));
  };

  return (
    <div className="flex items-center gap-2.5 overflow-x-auto px-4 pt-1 pb-0" style={{ height: 112 }}>
      {feed.ownStatus && myUid && (
        <StatusWantRing
          name={feed.ownStatus.name}
          photoUrl={feed.ownStatus.photoUrl}
          type={StatusWantType.status}
          // isSelf already forces every segment "unviewed" for the owner's
          // own ring, so no need for the local-viewed override here.
          segments={feed.ownStatus.segments}
          isSelf
          onTap={() => onOpen(StatusWantType.status, myUid)}
          onAddTap={onAddStatus}
        />
      )}
      {feed.others.map((item) => (
        <StatusWantRing
          key={`${item.type}-${item.uid}`}
          name={item.name}
          photoUrl={item.photoUrl}
          type={item.type}
          segments={effectiveSegments(item)}
          onTap={() => onOpen(item.type, item.uid)}
        />
      ))}
    </div>
  );
};

// ── Main PostScreen ──────────────────────────────────────────────────────────

export const PostScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const subscribe = useSubscribe();
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentTab, setCurrentTabState] = useState(() => {
    const param = searchParams.get('tab');
    return TAB_PARAM_TO_INDEX[param] ?? 0;
  });

  const setCurrentTab = useCallback((indexOrUpdater) => {
    setCurrentTabState((prev) => {
      const next = typeof indexOrUpdater === 'function' ? indexOrUpdater(prev) : indexOrUpdater;
      if (next !== prev) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('tab', TAB_INDEX_TO_PARAM[next] || 'filatom');
        setSearchParams(nextParams, { replace: true });
      }
      return next;
    });
  }, [searchParams, setSearchParams]);

  const [profile, setProfile] = useState(null);
  const [allListings, setAllListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [activeSubCategory, setActiveSubCategory] = useState(null);
  const [filterState, setFilterState] = useState(new FilterState());
  const [fabVisible, setFabVisible] = useState(true);
  const [lastScrollOffset, setLastScrollOffset] = useState(0);

  const [atomsDocs, setAtomsDocs] = useState([]);
  const [isLoadingAtomsInitial, setIsLoadingAtomsInitial] = useState(true);
  const [isLoadingAtomsMore, setIsLoadingAtomsMore] = useState(false);
  const [hasMoreAtoms, setHasMoreAtoms] = useState(true);
  const [currentAtomIndex, setCurrentAtomIndex] = useState(0);
  // Doc index of the currently-visible video (distinct from currentAtomIndex,
  // the raw feed-slide index, once recommendation slides are interleaved).
  const [currentAtomDocIndex, setCurrentAtomDocIndex] = useState(0);

  // ── Recommended users ────────────────────────────────────────────────────
  const [recommendedUsers, setRecommendedUsers] = useState([]);

  // ── Status / Want row ─────────────────────────────────────────────────────
  const [statusWantFeed, setStatusWantFeed] = useState(null);
  // Doc ids the viewer overlays have told us *this session* were just
  // viewed. Segments are greyed out from this set immediately — the ring
  // doesn't wait on (or race) the next full Firestore reload of the feed.
  const [locallyViewedDocIds, setLocallyViewedDocIds] = useState(() => new Set());
  // Which viewer overlay is open, if any: { type, uid } | null.
  const [statusWantViewer, setStatusWantViewer] = useState(null);

  const scrollRef = useRef(null);
  const atomsPageRef = useRef(null);
  const lastDocRef = useRef(null);
  const subscribeRef = useRef(subscribe);
  subscribeRef.current = subscribe;

  const LISTINGS_LIMIT = 20;
  const ATOMS_LIMIT = 20;

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const syncDesktopTab = (isDesktop) => {
      if (isDesktop) {
        setCurrentTab((prev) => (prev === 0 ? 1 : prev));
      }
    };
    syncDesktopTab(mq.matches);
    const handleChange = (e) => syncDesktopTab(e.matches);
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, [setCurrentTab]);

  useEffect(() => {
    const loadProfile = async () => {
      const cached = userProfileService.cachedProfile;
      if (cached) {
        setProfile(cached);
        return;
      }
      const profileData = await userProfileService.getCurrentUserProfile();
      if (profileData) setProfile(profileData);
    };
    loadProfile();
  }, []);

  // ── Load recommended users ───────────────────────────────────────────────
  //
  // Pulls a pool of candidate users, drops the current user and anyone
  // already subscribed to, then shuffles it once. Basic "standard"
  // recommendation baseline — a production system would rank by mutual
  // connections/engagement, but this keeps the feed from ever suggesting
  // someone the user already follows or themselves.

  const loadRecommendedUsers = useCallback(async () => {
    if (!user) return;
    try {
      const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(60));
      const snap = await getDocs(q);

      const eligible = snap.docs
        .filter((d) => d.id !== user.uid && !subscribeRef.current.isSubscribedTo(d.id))
        .map((d) => ({ id: d.id, ...d.data() }));

      // Fisher–Yates shuffle
      for (let i = eligible.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [eligible[i], eligible[j]] = [eligible[j], eligible[i]];
      }

      setRecommendedUsers(eligible);
    } catch (error) {
      console.error('PostScreen recommended users error:', error);
    }
  }, [user?.uid]); // only re-runs if the signed-in user actually changes

  useEffect(() => {
    loadRecommendedUsers();
  }, [loadRecommendedUsers]);

  // Returns a stable slice of `count` recommended users for the
  // recommendation card belonging to the given page/slot, rotating through
  // the pool so different cards don't repeat the same faces.
  const usersForRecommendationCard = useCallback(
    (pageIndex, count = 6) => {
      if (recommendedUsers.length === 0) return [];
      const total = recommendedUsers.length;
      if (total <= count) return recommendedUsers;
      const start = (pageIndex * count) % total;
      return Array.from({ length: count }, (_, i) => recommendedUsers[(start + i) % total]);
    },
    [recommendedUsers]
  );

  const openAllUsers = useCallback(() => {
    navigate('/users');
  }, [navigate]);

  // ── Status / Want feed ───────────────────────────────────────────────────

  const loadStatusWantFeed = useCallback(async () => {
    try {
      const result = await statusWantService.loadFeed();
      setStatusWantFeed(result);
    } catch (error) {
      console.error('PostScreen status/want feed error:', error);
    }
  }, []);

  // On web, Firebase restores the session asynchronously. Loading on mount
  // ran before the user existed (no own ring, every segment "unviewed"),
  // so load once the signed-in user is known — and reload if it changes.
  useEffect(() => {
    if (user?.uid) {
      loadStatusWantFeed();
    } else {
      setStatusWantFeed(null);
    }
  }, [user?.uid, loadStatusWantFeed]);

  // Routes a ring tap to the right viewer overlay. Views are marked by the
  // viewer overlays themselves as each item is actually displayed (opening
  // at the first unseen one, WhatsApp-style) — not here, since marking on
  // tap would record a user's own want as "viewed by them" the moment they
  // tapped it.
  //
  // Refuses to open when uid is missing — a falsy uid would reach
  // Firestore as `undefined` and blow up inside where()/doc() path
  // construction. Logged so a missing-uid ring is easy to trace.
  const openStatusWant = useCallback((type, uid) => {
    if (!uid) {
      console.warn('[StatusWant] openStatusWant called with empty uid', {
        type,
        uid,
      });
      return;
    }
    setStatusWantViewer({ type, uid });
  }, []);

  // The viewer overlays hand back the ids of whatever they marked viewed
  // this session so the ring can grey those segments immediately, ahead of
  // the background feed reload.
  const closeStatusWantViewer = useCallback(
    (viewedIds) => {
      setStatusWantViewer(null);
      if (Array.isArray(viewedIds) && viewedIds.length > 0) {
        setLocallyViewedDocIds((prev) => {
          const next = new Set(prev);
          viewedIds.forEach((id) => next.add(id));
          return next;
        });
      }
      loadStatusWantFeed();
    },
    [loadStatusWantFeed],
  );

  // The "+" badge on the current user's own ring — always opens the
  // add-status flow, never add-want.
  const goToAddStatus = useCallback(() => {
    navigate('/post/add-status');
  }, [navigate]);

  // ── Post add options (Listing / Want / Status) ───────────────────────────
  //
  // Routes the option chosen from the pop-out PostAddFab menu to the right
  // screen. Wants and Status both post straight to Firestore with no
  // verification gate, unlike Listings which route through AddPropertyPage's
  // own seller-verification flow.

  const handlePostAddSelect = useCallback(
    (option) => {
      if (option === PostAddOption.LISTING) navigate('/post/add-property');
      else if (option === PostAddOption.WANT) navigate('/post/add-want');
      else if (option === PostAddOption.STATUS) navigate('/post/add-status');
    },
    [navigate],
  );

  // ── Load listings ─────────────────────────────────────────────────────────

  const loadListings = useCallback(async () => {
    setIsLoadingInitial(true);
    try {
      const q = query(
        collection(db, 'listings'),
        orderBy('createdAt', 'desc'),
        limit(LISTINGS_LIMIT)
      );
      const snap = await getDocs(q);
      const docs = snap.docs;
      setAllListings(docs);
      setHasMore(docs.length === LISTINGS_LIMIT);
      if (docs.length > 0) {
        lastDocRef.current = docs[docs.length - 1];
      }
      rebuildFilteredList(docs, activeSubCategory, filterState);
    } catch (error) {
      console.error('PostScreen listings error:', error);
    } finally {
      setIsLoadingInitial(false);
    }
  }, [activeSubCategory, filterState]);

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  const loadMoreListings = useCallback(async () => {
    if (isLoadingMore || !hasMore || allListings.length === 0) return;
    setIsLoadingMore(true);
    try {
      const q = query(
        collection(db, 'listings'),
        orderBy('createdAt', 'desc'),
        startAfter(lastDocRef.current),
        limit(LISTINGS_LIMIT)
      );
      const snap = await getDocs(q);
      const newDocs = snap.docs;
      if (newDocs.length > 0) {
        const updated = [...allListings, ...newDocs];
        setAllListings(updated);
        setHasMore(newDocs.length === LISTINGS_LIMIT);
        lastDocRef.current = newDocs[newDocs.length - 1];
        rebuildFilteredList(updated, activeSubCategory, filterState);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('PostScreen load more error:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [allListings, hasMore, isLoadingMore, activeSubCategory, filterState]);

  const rebuildFilteredList = (listings, subCategory, filter) => {
    const result = listings
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(listing => {
        if (subCategory) {
          if ((listing.subcategory || '') !== subCategory) return false;
        }
        if (!filter.isEmpty) {
          if (!filter.matches(listing)) return false;
        }
        return true;
      });
    setFilteredListings(result);
  };

  // ── Atoms loading ─────────────────────────────────────────────────────────

  const loadAtoms = useCallback(async () => {
    setIsLoadingAtomsInitial(true);
    try {
      const q = query(
        collection(db, 'listings'),
        where('hasVideo', '==', true),
        orderBy('createdAt', 'desc'),
        limit(ATOMS_LIMIT)
      );
      const snap = await getDocs(q);
      setAtomsDocs(snap.docs);
      setHasMoreAtoms(snap.docs.length === ATOMS_LIMIT);
    } catch (error) {
      console.error('PostScreen atoms error:', error);
    } finally {
      setIsLoadingAtomsInitial(false);
    }
  }, []);

  useEffect(() => {
    loadAtoms();
  }, [loadAtoms]);

  const loadMoreAtoms = useCallback(async () => {
    if (isLoadingAtomsMore || !hasMoreAtoms || atomsDocs.length === 0) return;
    setIsLoadingAtomsMore(true);
    try {
      const last = atomsDocs[atomsDocs.length - 1];
      const q = query(
        collection(db, 'listings'),
        where('hasVideo', '==', true),
        orderBy('createdAt', 'desc'),
        startAfter(last),
        limit(ATOMS_LIMIT)
      );
      const snap = await getDocs(q);
      if (snap.docs.length > 0) {
        setAtomsDocs([...atomsDocs, ...snap.docs]);
        setHasMoreAtoms(snap.docs.length === ATOMS_LIMIT);
      } else {
        setHasMoreAtoms(false);
      }
    } catch (error) {
      console.error('PostScreen atoms load more error:', error);
    } finally {
      setIsLoadingAtomsMore(false);
    }
  }, [atomsDocs, hasMoreAtoms, isLoadingAtomsMore]);

  // ── Atoms feed sequence ───────────────────────────────────────────────────
  //
  // Builds the combined Atoms sequence: real video docs with a single
  // recommendation slide inserted per FEED_PAGE_SIZE-doc page, at a
  // position seeded purely by page index — mirrors Flutter's
  // _buildAtomFeedItems exactly, so the same doc never moves between
  // rebuilds and already-loaded pages never re-shuffle when more atoms
  // are appended.

  const buildAtomFeedItems = useCallback(() => {
    const items = [];
    const total = atomsDocs.length;
    if (total === 0) return items;

    let start = 0;
    let pageIndex = 0;
    while (start < total) {
      const end = Math.min(start + FEED_PAGE_SIZE, total);
      const pageLength = end - start;

      let recOffset = null;
      if (recommendedUsers.length > 0 && pageLength > 6) {
        const rnd = seededRandom(pageIndex * 104729 + 31);
        const lo = 3;
        const hi = pageLength - 3;
        if (hi > lo) recOffset = lo + Math.floor(rnd() * (hi - lo));
      }

      for (let i = start; i < end; i++) {
        if (recOffset !== null && i - start === recOffset) {
          items.push({ type: 'recommendation', pageIndex });
        }
        items.push({ type: 'video', docIndex: i });
      }

      start = end;
      pageIndex++;
    }
    return items;
  }, [atomsDocs, recommendedUsers]);

  // ── Scroll handler ────────────────────────────────────────────────────────

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const offset = scrollRef.current.scrollTop;
    const delta = offset - lastScrollOffset;

    if (delta > 10 && fabVisible) {
      setFabVisible(false);
    } else if (delta < -10 && !fabVisible) {
      setFabVisible(true);
    }

    const element = scrollRef.current;
    if (element.scrollTop + element.clientHeight >= element.scrollHeight - 400) {
      if (!isLoadingMore && hasMore) {
        loadMoreListings();
      }
    }

    setLastScrollOffset(offset);
  }, [fabVisible, lastScrollOffset, isLoadingMore, hasMore, loadMoreListings]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSubCategorySelected = (subCategory) => {
    setActiveSubCategory(subCategory);
    rebuildFilteredList(allListings, subCategory, filterState);
    setCurrentTab(1);
  };

  const handleClearFilter = () => {
    setActiveSubCategory(null);
    setFilterState(new FilterState());
    rebuildFilteredList(allListings, null, new FilterState());
  };

  const handleFilterApply = (result) => {
    setFilterState(result);
    rebuildFilteredList(allListings, activeSubCategory, result);
  };

  const handleRefresh = async () => {
    await loadListings();
    loadStatusWantFeed();
  };

  // Tracks both the raw feed-slide index (for scroll bookkeeping) and the
  // underlying video doc index (for VideoItem's `isActive` + pagination),
  // since the two diverge once recommendation slides are interleaved.
  const handleAtomsScroll = useCallback(() => {
    const el = atomsPageRef.current;
    if (!el || el.clientHeight === 0) return;
    const feedIndex = Math.round(el.scrollTop / el.clientHeight);
    setCurrentAtomIndex(feedIndex);

    const items = buildAtomFeedItems();
    const item = items[feedIndex];
    if (item && item.type === 'video') {
      const docIndex = item.docIndex;
      setCurrentAtomDocIndex(docIndex);
      if (docIndex >= atomsDocs.length - 3 && !isLoadingAtomsMore && hasMoreAtoms) {
        loadMoreAtoms();
      }
    }
  }, [buildAtomFeedItems, atomsDocs.length, isLoadingAtomsMore, hasMoreAtoms, loadMoreAtoms]);

  // ── Render welcome tab ────────────────────────────────────────────────────

  const renderWelcomeTab = () => {
    const greeting = buildGreeting(profile?.displayName || '');

    return (
      <div className="px-3 pt-4 pb-32 overflow-y-auto h-full" style={{ paddingTop: 80 }}>
        <h2 className="text-white font-bold text-sm mb-2.5">{greeting}</h2>

        <div className={`grid grid-cols-2 ${GRID_GAP}`}>
          <CategoryCard category={propertyCategories[0]} categoryIndex={0} onSubCategorySelected={handleSubCategorySelected} />
          <CategoryCard category={propertyCategories[1]} categoryIndex={1} onSubCategorySelected={handleSubCategorySelected} />
        </div>

        <div className={`grid grid-cols-3 ${GRID_GAP} mt-1.5`}>
          <CategoryCard category={propertyCategories[2]} categoryIndex={2} onSubCategorySelected={handleSubCategorySelected} />
          <CategoryCard category={propertyCategories[6]} categoryIndex={6} onSubCategorySelected={handleSubCategorySelected} />
          <CategoryCard category={propertyCategories[3]} categoryIndex={3} onSubCategorySelected={handleSubCategorySelected} />
        </div>

        <div className={`grid grid-cols-2 ${GRID_GAP} mt-1.5`}>
          <CategoryCard category={propertyCategories[4]} categoryIndex={4} onSubCategorySelected={handleSubCategorySelected} />
          <CategoryCard category={propertyCategories[5]} categoryIndex={5} onSubCategorySelected={handleSubCategorySelected} />
        </div>

        <div className={`grid grid-cols-3 ${GRID_GAP} mt-1.5`}>
          <CategoryCard category={propertyCategories[7]} categoryIndex={7} onSubCategorySelected={handleSubCategorySelected} />
          <CategoryCard category={propertyCategories[8]} categoryIndex={8} onSubCategorySelected={handleSubCategorySelected} />
          <CategoryCard category={propertyCategories[9]} categoryIndex={9} onSubCategorySelected={handleSubCategorySelected} />
        </div>

        <div className="mt-2.5">
          <HomeAdItem />
        </div>
      </div>
    );
  };

  // ── Render posted feed ────────────────────────────────────────────────────

  const renderPostedFeed = () => {
    if (isLoadingInitial) {
      return (
        <div className="flex items-center justify-center h-full" style={{ paddingTop: 80 }}>
          <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (filteredListings.length === 0) {
      return (
        <div className="flex items-center justify-center h-full px-4" style={{ paddingTop: 80 }}>
          <div className="flex flex-col items-center gap-4">
            <svg className="w-16 h-16 text-white/12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1" />
            </svg>
            <p className="text-white/35 text-center text-sm font-medium">
              {activeSubCategory
                ? `No listings for "${activeSubCategory}"`
                : filterState.activeCount > 0
                ? 'No listings match your filters'
                : 'No listings yet'}
            </p>
            {(activeSubCategory || filterState.activeCount > 0) && (
              <button
                onClick={handleClearFilter}
                className="px-4 py-2 rounded-full text-xs font-semibold border border-filatom-primary/35"
                style={{ backgroundColor: `${COLORS.orange}1F`, color: COLORS.orange }}
              >
                Clear filter
              </button>
            )}
            {!activeSubCategory && filterState.activeCount === 0 && (
              <p className="text-white/20 text-xs">Tap + to add the first property</p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-full overflow-y-auto"
        style={{ paddingTop: 80, paddingBottom: 120 }}
      >
        <div className="hidden lg:block px-8 pt-2 pb-4">
          <div className="w-full rounded-2xl overflow-hidden" style={{ height: DESKTOP_AD_BANNER_HEIGHT }}>
            <HomeAdItem />
          </div>
        </div>

        {/* Status / Want row — current user's own status (if any) with an
            "add status" plus badge, then everyone else's active status/want
            rings — unviewed & newest first, viewed last. Renders nothing
            (no gap left behind) when there's nothing to show, so the
            recommended card below naturally sits at the top. */}
        <StatusWantRow
          feed={statusWantFeed}
          myUid={user?.uid}
          locallyViewedDocIds={locallyViewedDocIds}
          onOpen={openStatusWant}
          onAddStatus={goToAddStatus}
        />

        <div className="flex items-center justify-between px-4 pb-3 lg:px-8">
          <div className="flex-1 min-w-0">
            {activeSubCategory ? (
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border"
                style={{ backgroundColor: `${COLORS.orange}1F`, borderColor: `${COLORS.orange}66` }}
              >
                <svg className="w-3.5 h-3.5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="text-filatom-primary text-xs font-semibold">{activeSubCategory}</span>
                <button onClick={handleClearFilter} className="text-filatom-primary">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <span className="text-white text-sm font-bold">What are you looking for?</span>
            )}
          </div>
          <FilterIconButton current={filterState} onApply={handleFilterApply} />
        </div>

        {/* Recommended users — pinned once at the top, never mixed into the
            grid below, so the grid stays a single uninterrupted list and
            scroll order can never drift. */}
        {recommendedUsers.length > 0 && (
          <RecommendedUsersCard users={usersForRecommendationCard(0)} onSeeMore={openAllUsers} />
        )}

        <div className="grid grid-cols-2 gap-1.5 px-2.5 lg:grid-cols-4 lg:gap-4 lg:px-8 xl:grid-cols-5">
          {filteredListings.map((listing) => (
            <ListingItem key={listing.id} listing={listing} />
          ))}
        </div>

        {isLoadingMore && (
          <div className="flex justify-center py-5">
            <div className="w-6 h-6 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    );
  };

  // ── Render atoms tab ──────────────────────────────────────────────────────

  const renderAtomsTab = () => {
    if (isLoadingAtomsInitial) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (atomsDocs.length === 0) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-4">
            <svg className="w-16 h-16 text-white/12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <p className="text-white/35 text-center text-sm font-medium">No Atoms yet</p>
            <p className="text-white/20 text-xs">Property videos will appear here</p>
          </div>
        </div>
      );
    }

    const feedItems = buildAtomFeedItems();

    return (
      <div className="h-full overflow-hidden lg:flex lg:justify-center lg:pt-6">
        <div
          ref={atomsPageRef}
          onScroll={handleAtomsScroll}
          className="h-full overflow-y-scroll snap-y snap-mandatory scroll-smooth lg:max-w-md lg:w-full lg:rounded-2xl"
          style={{ scrollSnapType: 'y mandatory' }}
        >
          {feedItems.map((item) => {
            if (item.type === 'recommendation') {
              return (
                <div key={`atom_rec_page_${item.pageIndex}`} className="h-full snap-start snap-always">
                  <AtomRecommendedUsersCard
                    users={usersForRecommendationCard(item.pageIndex)}
                    onSeeMore={openAllUsers}
                  />
                </div>
              );
            }

            const doc = atomsDocs[item.docIndex];
            const listing = { id: doc.id, ...doc.data() };
            return (
              <div key={listing.id} className="h-full snap-start snap-always">
                <VideoItem
                  listing={listing}
                  isActive={currentTab === 2 && item.docIndex === currentAtomDocIndex}
                  bottomNavBarHeight={0}
                />
              </div>
            );
          })}
          {hasMoreAtoms && (
            <div className="h-full flex items-center justify-center snap-start snap-always">
              <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────

  const isAtomsTab = currentTab === 2;

  return (
    <div className="min-h-screen bg-filatom-bg relative lg:pl-[340px]">
      <DesktopCategorySidebar
        activeSubCategory={activeSubCategory}
        onSubCategorySelected={handleSubCategorySelected}
        onClearFilter={handleClearFilter}
        railWidth={DESKTOP_RAIL_WIDTH}
        width={DESKTOP_SIDEBAR_WIDTH}
      />

      <div className="h-screen overflow-hidden">
        {currentTab === 0 && renderWelcomeTab()}
        {currentTab === 1 && renderPostedFeed()}
        {currentTab === 2 && renderAtomsTab()}
      </div>

      <div
        className="absolute top-0 left-0 right-0 pt-safe-top px-4 z-10 lg:left-[340px] lg:px-8"
        style={{ paddingTop: 16 }}
      >
        <div className="flex items-center justify-between">
          <div
            className="flex items-center gap-1 p-1 rounded-full"
            style={{ backgroundColor: 'rgba(35,38,44,0.3)', backdropFilter: 'blur(24px)', border: '1px solid #23262C' }}
          >
            <div className="contents lg:hidden">
              <TabPill label="Filatom" isActive={currentTab === 0} onTap={() => setCurrentTab(0)} />
            </div>
            <TabPill label="Posted" isActive={currentTab === 1} onTap={() => setCurrentTab(1)} />
            <TabPill label="Atoms" isActive={currentTab === 2} onTap={() => setCurrentTab(2)} />
          </div>

          <button
            onClick={() => navigate('/search')}
            className="hidden lg:flex flex-1 mx-6 items-center gap-2 px-4 py-2.5 rounded-full text-left"
            style={{ backgroundColor: 'rgba(35,38,44,0.3)', backdropFilter: 'blur(24px)', border: '1px solid #23262C', maxWidth: 420 }}
          >
            <svg className="w-4 h-4 text-white/40 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="text-white/35 text-sm flex-1">Search Filatom</span>
          </button>

          <div className="contents lg:hidden">
            <button
              onClick={() => navigate('/search')}
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(35,38,44,0.3)', backdropFilter: 'blur(24px)', border: '1px solid #23262C' }}
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Post add FAB — Listing / Want / Status pop-out. Mobile-only, same
          as before (`lg:hidden`); desktop keeps its own add entry points
          elsewhere in the layout. PostAddFab renders itself as
          `position: fixed` at the viewport level with a high z-index, so
          it now clears the app's bottom nav bar instead of sitting behind
          it — see the component for the one number to tune if your
          BottomNavBar isn't ~64px tall. */}
      {!isAtomsTab && (
        <div className="lg:hidden">
          <PostAddFab visible={fabVisible} onSelect={handlePostAddSelect} />
        </div>
      )}

      {/* Status / Want viewer overlays.
          Also checks `statusWantViewer.uid` is truthy — a stale/invalid
          state can never mount a viewer without a uid, which would reach
          Firestore as undefined and crash inside where()/doc(). */}
      {statusWantViewer?.type === StatusWantType.status &&
        statusWantViewer.uid && (
          <StatusViewerScreen
            key={`status-${statusWantViewer.uid}`}
            uid={statusWantViewer.uid}
            onClose={closeStatusWantViewer}
            onOpenProfile={(uid) => navigate(`/profile/${uid}`)}
          />
        )}
      {statusWantViewer?.type === StatusWantType.want &&
        statusWantViewer.uid && (
          <WantViewerScreen
            key={`want-${statusWantViewer.uid}`}
            uid={statusWantViewer.uid}
            onClose={closeStatusWantViewer}
            onOpenProfile={(uid) => navigate(`/profile/${uid}`)}
            onOpenChat={(payload) => navigate(`/chats/${payload.conversationId}`, { state: payload })}
          />
        )}
    </div>
  );
};

export default PostScreen;